/**
 * Service pour les opérations de maintenance (base de données, intégrations, tokens, logs, santé)
 * @module admin/services/maintenanceService
 */

import { apiGet, apiPost } from '../../../utils/api';

// ============================================
// BASE DE DONNÉES
// ============================================

/**
 * Récupère les statistiques de la base de données
 * @returns {Promise<Object>} Statistiques avec data contenant les stats
 * @throws {Error} Si la récupération échoue
 */
export const getDatabaseStats = async () => {
  return await apiGet('/admin/maintenance/database/stats');
};

/**
 * Nettoie la base de données (dry run ou réel)
 * @param {Object} cleanupOptions - Options de nettoyage (daysToKeep, tables)
 * @param {boolean} dryRun - Si true, simule le nettoyage sans supprimer
 * @returns {Promise<Object>} Résultat du nettoyage
 * @throws {Error} Si le nettoyage échoue
 */
export const cleanupDatabase = async (cleanupOptions, dryRun = false) => {
  const endpoint = dryRun
    ? '/admin/maintenance/database/cleanup-dry-run'
    : '/admin/maintenance/database/cleanup';
  return await apiPost(endpoint, cleanupOptions);
};

/**
 * Liste les tables de la BDD avec leur nombre de lignes (vue rapide BDD)
 * @returns {Promise<Object>} { success, data: { tables: [{ name, rowCount }] } }
 */
export const getDatabaseTables = async () => {
  return await apiGet('/admin/maintenance/database/tables');
};

/**
 * Aperçu paginé d'une table (lecture seule)
 * @param {string} tableName - Nom de la table
 * @param {number} limit - Nombre de lignes (défaut 50)
 * @param {number} offset - Décalage pour pagination
 * @returns {Promise<Object>} { success, data: { columns, rows, totalCount, limit, offset } }
 */
export const getTablePreview = async (tableName, limit = 50, offset = 0) => {
  return await apiGet(
    `/admin/maintenance/database/tables/${encodeURIComponent(tableName)}?limit=${limit}&offset=${offset}`
  );
};

// ============================================
// INTÉGRATIONS
// ============================================

/**
 * Récupère le statut des intégrations (Twitch, Spotify)
 * @returns {Promise<Object>} Statut des intégrations avec data
 * @throws {Error} Si la récupération échoue
 */
export const getIntegrationStatus = async () => {
  return await apiGet('/admin/maintenance/integrations/status');
};

/**
 * Teste une intégration (Twitch ou Spotify)
 * @param {string} service - 'twitch' ou 'spotify'
 * @returns {Promise<Object>} Résultat du test
 * @throws {Error} Si le test échoue
 */
export const testIntegration = async (service) => {
  const endpoint =
    service === 'twitch'
      ? '/admin/maintenance/integrations/test-twitch'
      : '/admin/maintenance/integrations/test-spotify';
  return await apiPost(endpoint);
};

/**
 * Teste la connexion SMTP et envoie un email de test
 * @param {string} [email] - Adresse de destination (optionnel, sinon MAIL_SUPERVISION)
 * @returns {Promise<Object>} Résultat du test
 * @throws {Error} Si le test échoue
 */
export const testMail = async (email) => {
  return await apiPost('/admin/maintenance/integrations/test-mail', {
    ...(email && { email }),
  });
};

// ============================================
// TOKENS
// ============================================

/**
 * Récupère le statut des tokens (Twitch, Spotify)
 * @returns {Promise<Object>} Statut des tokens avec data
 * @throws {Error} Si la récupération échoue
 */
export const getTokenStatus = async () => {
  return await apiGet('/admin/maintenance/tokens/status');
};

/**
 * Rafraîchit un token (Twitch ou Spotify)
 * @param {string} service - 'twitch' ou 'spotify'
 * @returns {Promise<Object>} Résultat du rafraîchissement
 * @throws {Error} Si le rafraîchissement échoue
 */
export const refreshToken = async (service) => {
  const endpoint =
    service === 'twitch'
      ? '/admin/maintenance/tokens/refresh-twitch'
      : '/admin/maintenance/tokens/refresh-spotify';
  return await apiPost(endpoint);
};

// ============================================
// LOGS
// ============================================

/**
 * Récupère la liste des fichiers de logs
 * @returns {Promise<Object>} Liste des fichiers avec success et data.files
 * @throws {Error} Si la récupération échoue
 */
export const getLogFiles = async () => {
  return await apiGet('/admin/maintenance/logs/list');
};

/**
 * Récupère le contenu d'un fichier de log
 * @param {string} filename - Nom du fichier de log (peut être un chemin relatif comme "auth/general-2026-01-18.log")
 * @returns {Promise<Object>} Contenu du fichier avec success et data
 * @throws {Error} Si la récupération échoue
 */
export const getLogContent = async (filename) => {
  // Encoder chaque segment du chemin pour gérer les chemins relatifs
  const encodedPath = filename
    .split('/')
    .map(segment => encodeURIComponent(segment))
    .join('/');
  return await apiGet(`/admin/maintenance/logs/${encodedPath}`);
};

/**
 * Nettoie les anciens fichiers de logs
 * @param {number} daysToKeep - Nombre de jours à conserver
 * @returns {Promise<Object>} Résultat du nettoyage avec deletedFiles
 * @throws {Error} Si le nettoyage échoue
 */
export const cleanupLogs = async (daysToKeep = 7) => {
  return await apiPost('/admin/maintenance/logs/cleanup', {
    daysToKeep,
  });
};

// ============================================
// SANTÉ SYSTÈME
// ============================================

/**
 * Récupère le rapport de santé au démarrage
 * @returns {Promise<Object>} Rapport de santé avec success et data
 * @throws {Error} Si la récupération échoue
 */
export const getStartupHealth = async () => {
  return await apiGet('/admin/maintenance/health/startup');
};

/**
 * Récupère l'état de santé actuel du système
 * @returns {Promise<Object>} État de santé avec success et data
 * @throws {Error} Si la récupération échoue
 */
export const getCurrentHealth = async () => {
  return await apiGet('/admin/maintenance/health/current');
};

/**
 * Rafraîchit l'état de santé du système
 * @returns {Promise<Object>} Nouvel état de santé avec success et data
 * @throws {Error} Si le rafraîchissement échoue
 */
export const refreshHealth = async () => {
  return await apiPost('/admin/maintenance/health/refresh');
};

// ============================================
// ANNIVERSAIRES
// ============================================

/**
 * Récupère le statut du service d'anniversaires
 * @returns {Promise<Object>} Statut avec success et data
 * @throws {Error} Si la récupération échoue
 */
export const getBirthdayStatus = async () => {
  return await apiGet('/admin/maintenance/birthdays/status');
};

/**
 * Récupère l'historique des annonces d'anniversaires
 * @param {number} limit - Nombre maximum d'annonces à récupérer (défaut: 30)
 * @returns {Promise<Object>} Historique avec success et data
 * @throws {Error} Si la récupération échoue
 */
export const getBirthdayHistory = async (limit = 30) => {
  return await apiGet(`/admin/maintenance/birthdays/history?limit=${limit}`);
};

/**
 * Force l'envoi d'une annonce d'anniversaire (mode test admin)
 * Ignore l'anti-doublon et envoie réellement le message dans le chat
 * @returns {Promise<Object>} Résultat avec success et data
 * @throws {Error} Si l'envoi échoue
 */
export const testBirthdayAnnounce = async () => {
  return await apiPost('/admin/maintenance/birthdays/test-announce');
};

/**
 * Vérifie les anniversaires et envoie l'annonce si nécessaire (avec anti-doublon)
 * @returns {Promise<Object>} Résultat avec success et data
 * @throws {Error} Si la vérification échoue
 */
export const checkBirthdays = async () => {
  return await apiPost('/admin/maintenance/birthdays/check');
};
